/**
 * 세션 결제 진행 계산 — 상태 화면의 계산부 (menu #819 M5)
 *
 * ## 분류는 서버가 한다
 *
 * `payment_state` 는 admin `SessionBillContract::paymentState()` 가 **이미 접어서**
 * 내리는 3분류다. FE 가 `status` 로 다시 유도하면 판정이 두 벌이 되고, 두 벌은 반드시
 * 갈린다 — 실제로 `void` 에서 갈렸다(서버 `pendiente` vs 재유도 `pagado`). 받지도 않은
 * 돈을 받은 것으로 세면 진행바가 거짓말을 한다.
 *
 * 그래서 여기서는 **더하기만** 한다.
 *
 * - `pagado` — 끝났다. 할 일 없음.
 * - `en_caja` — 카운터에서 처리 중이다. 기다리면 된다. ★ 이걸 `pendiente` 로 묶으면
 *   이미 카드를 건넨 손님이 또 낸다.
 * - `pendiente` — 아직 안 냈다.
 *
 * ## 진행바는 금액 기준이다
 *
 * 사람 수 기준으로 그리면, 큰 몫을 진 사람이 안 낸 상태에서 "5명 중 4명 완료" 가
 * 되어 거의 다 끝난 것처럼 보인다. 남은 돈이 진실이다.
 */
import type { BillPaymentState, SessionBill } from '@/lib/api/split-payment';

export type BillProgressState = BillPaymentState;

/**
 * 이 계산서가 손님에게 어떤 상태인가 — 서버 판정을 그대로 쓴다.
 *
 * 모르는 값은 `pendiente` 로 본다. 서버가 상태를 추가했을 때 FE 가 그것을 "냈음" 으로
 * 오인하면 **받지 않은 돈이 받은 것으로 집계된다** — 모를 때는 안 받은 쪽이 안전하다.
 */
export function classifyBill(bill: SessionBill): BillProgressState {
  switch (bill.payment_state) {
    case 'pagado':
    case 'en_caja':
      return bill.payment_state;
    default:
      return 'pendiente';
  }
}

export interface SessionProgress {
  /** 이미 받은 금액(주 단위, `SessionBill` 이 주는 단위 그대로). */
  paidAmount: number;
  /** 카운터에서 처리 중인 금액 — 아직 받지 않았지만 손님이 또 낼 것도 아니다. */
  inCashierAmount: number;
  pendingAmount: number;
  totalAmount: number;
  /** 0~100. 총액이 0 이면 100 으로 본다(낼 것이 없으면 끝난 것이다). */
  paidPercent: number;
}

/**
 * 세션 전체 진행.
 *
 * 잔여 Bill 도 포함한다 — 아무도 가져가지 않은 몫이야말로 "아직 안 낸 돈" 이다.
 */
export function summarizeProgress(bills: readonly SessionBill[]): SessionProgress {
  let paidAmount = 0;
  let inCashierAmount = 0;
  let pendingAmount = 0;

  for (const bill of bills) {
    const amount = bill.total_amount;

    switch (classifyBill(bill)) {
      case 'pagado':
        paidAmount += amount;
        break;
      case 'en_caja':
        inCashierAmount += amount;
        break;
      case 'pendiente':
        pendingAmount += amount;
        break;
    }
  }

  const totalAmount = paidAmount + inCashierAmount + pendingAmount;

  return {
    paidAmount,
    inCashierAmount,
    pendingAmount,
    totalAmount,
    paidPercent: totalAmount === 0 ? 100 : Math.round((paidAmount / totalAmount) * 100),
  };
}
